import { useState, useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons/faCheckCircle";
import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons/faCalendarAlt";
import { faChartLine } from "@fortawesome/free-solid-svg-icons/faChartLine";
import { faShieldAlt } from "@fortawesome/free-solid-svg-icons/faShieldAlt";
import { faPiggyBank } from "@fortawesome/free-solid-svg-icons/faPiggyBank";
import { faUsers } from "@fortawesome/free-solid-svg-icons/faUsers";
import { faFileInvoiceDollar } from "@fortawesome/free-solid-svg-icons/faFileInvoiceDollar";
import { faLightbulb } from "@fortawesome/free-solid-svg-icons/faLightbulb";
import { faPhoneAlt } from "@fortawesome/free-solid-svg-icons/faPhoneAlt";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons/faArrowRight";
import { faArrowUp } from "@fortawesome/free-solid-svg-icons/faArrowUp";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Helmet } from "react-helmet-async";
import "./FAQ.css";

const faqs = Object.freeze([
  {
    question: "What is a financial advisor, and why do I need one?",
    answer: (
      <>
        <p>
          A financial advisor helps you plan and manage your financial future by
          providing expert guidance on investments, retirement, insurance, and
          tax strategies.
        </p>
        <div className="faq-icons-row">
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faCheckCircle} />
            <span>Personalised financial planning</span>
          </div>
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faChartLine} />
            <span>Guidance on tax-efficient investments</span>
          </div>
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faCalendarAlt} />
            <span>Regular portfolio reviews</span>
          </div>
        </div>
      </>
    ),
  },
  {
    question: "How do I schedule a free financial review?",
    answer: (
      <>
        <p>
          Scheduling a free financial review is easy! Just contact us via phone
          or email.
        </p>
        <div className="faq-icons-row">
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faPhoneAlt} />
            <span>One-on-one consultations with experts</span>
          </div>
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faCheckCircle} />
            <span>No hidden costs, no obligations</span>
          </div>
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faCalendarAlt} />
            <span>In-depth financial health check</span>
          </div>
        </div>
      </>
    ),
  },
  // {
  //   question: "What investment options do you offer?",
  //   answer: (
  //     <>
  //       We offer tailored investment plans, including:
  //       <br />
  //       <FontAwesomeIcon icon={faPiggyBank} /> Stocks, Bonds, Mutual Funds, ETFs
  //       <br />
  //       <FontAwesomeIcon icon={faChartLine} /> Real Estate & Alternative
  //       Investments
  //       <br />
  //       <FontAwesomeIcon icon={faCalendarAlt} /> Retirement-focused investments
  //     </>
  //   ),
  // },
  {
    question: "Can you help with retirement planning?",
    answer: (
      <>
        <p>
          Yes! We assist with pension management, annuities, and personalised
          retirement plans to ensure financial security.
        </p>
        <div className="faq-icons-row">
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faShieldAlt} />
            <span>Creating sustainable retirement income</span>
          </div>
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faFileInvoiceDollar} />
            <span>Maximising tax benefits on retirement funds</span>
          </div>
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faLightbulb} />
            <span>Future-proofing your lifestyle goals</span>
          </div>
        </div>
      </>
    ),
  },
  {
    question: "How does tax optimisation work?",
    answer: (
      <>
        <p>
          We implement legal tax strategies to reduce your tax burden,
          including:
        </p>
        <div className="faq-icons-row">
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faFileInvoiceDollar} />
            <span>Tax-efficient investments</span>
          </div>
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faShieldAlt} />
            <span>Estate planning & inheritance tax strategies</span>
          </div>
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faCalendarAlt} />
            <span>Retirement account tax benefits</span>
          </div>
        </div>
      </>
    ),
  },
  // {
  //   question: "What types of insurance do you recommend?",
  //   answer: (
  //     <>
  //       We recommend:
  //       <br />
  //       <FontAwesomeIcon icon={faShieldAlt} /> Life Insurance – Protect your
  //       family’s financial future
  //       <br />
  //       <FontAwesomeIcon icon={faCheckCircle} /> Income Protection – Cover your
  //       earnings in case of disability
  //       <br />
  //       <FontAwesomeIcon icon={faShieldAlt} /> Mortgage Protection – Ensure your
  //       Home is covered.
  //     </>
  //   ),
  // },
  {
    question: "Are your financial advisory services personalised?",
    answer: (
      <>
        <p>
          Absolutely! We assess your financial situation, goals, and risk
          tolerance to create a tailored strategy that suits your needs.
        </p>
        <div className="faq-icons-row">
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faUsers} />
            <span>One-on-one financial assessment</span>
          </div>
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faChartLine} />
            <span>Customised investment portfolios</span>
          </div>
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faLightbulb} />
            <span>Long-term financial growth strategies</span>
          </div>
        </div>
      </>
    ),
  },
  {
    question: "Do I need a lot of money to start investing?",
    answer: (
      <>
        <p>
          No, you don’t need a large sum to start investing. Apollo Financial
          Services helps clients with different financial backgrounds.
        </p>
        <div className="faq-icons-row">
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faPiggyBank} />
            <span>Low-barrier investment options</span>
          </div>
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faCheckCircle} />
            <span>Step-by-step investment guidance</span>
          </div>
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faLightbulb} />
            <span>Budget-friendly investment strategies</span>
          </div>
        </div>
      </>
    ),
  },
  {
    question: "How often should I review my financial plan?",
    answer: (
      <>
        <p>
          We recommend an annual financial review, but adjustments should be
          made whenever there’s a change in financial goals.
        </p>
        <div className="faq-icons-row">
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faCalendarAlt} />
            <span>Annual financial check-ups</span>
          </div>
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faChartLine} />
            <span>Market trend analysis</span>
          </div>
          <div className="faq-icon-item">
            <FontAwesomeIcon icon={faFileInvoiceDollar} />
            <span>Adjustments based on economic conditions</span>
          </div>
        </div>
      </>
    ),
  },
]);

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [heroRef, heroInView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const toggleFAQ = useCallback((index: number) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  }, []);

  const fadeInVariants = useMemo(
    () => ({
      hidden: { opacity: 0 },
      visible: { opacity: 1, transition: { duration: 0.8 } },
    }),
    []
  );

  const renderedFAQs = useMemo(
    () =>
      faqs.map((faq, index) => (
        <div
          key={index}
          className={`faq-item ${activeIndex === index ? "open" : ""}`}
        >
          <div className="faq-content">
            <button className="faq-question" onClick={() => toggleFAQ(index)}>
              {faq.question}
              <span className="faq-icon">
                <FontAwesomeIcon
                  icon={activeIndex === index ? faArrowUp : faArrowRight}
                />
              </span>
            </button>
            <AnimatePresence>
              {activeIndex === index && (
                <div className="faq-answer">{faq.answer}</div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )),
    [activeIndex, toggleFAQ]
  );

  return (
    <div className="faq-page">
      <Helmet>
        <title>
          FAQ's - Apollo Financial Services | Apollo Financial Services Ireland
        </title>
        <meta
          name="description"
          content="Get answers to common financial planning questions from Apollo Financial Services experts."
        />
        <meta
          name="keyword"
          content="financial advisor Limerick, financial planner Limerick, wealth management Limerick, investment advisor Limerick, retirement planning Limerick, financial consultant Limerick, personal finance advisor Limerick, financial planning services Limerick, independent financial advisor Limerick, fiduciary financial advisor Limerick, financial advisor Waterford, financial planner Waterford, wealth management Waterford, investment advisor Waterford, retirement planning Waterford, financial consultant Waterford, personal finance advisor Waterford, financial planning services Waterford, independent financial advisor Waterford, fiduciary financial advisor Waterford, financial advisor Kilkenny, financial planner Kilkenny, wealth management Kilkenny, investment advisor Kilkenny, retirement planning Kilkenny, financial consultant Kilkenny, personal finance advisor Kilkenny, financial planning services Kilkenny, independent financial advisor Kilkenny, fiduciary financial advisor Kilkenny, financial advisor Sligo, financial planner Sligo, wealth management Sligo, investment advisor Sligo, retirement planning Sligo, financial consultant Sligo, personal finance advisor Sligo, financial planning services Sligo, independent financial advisor Sligo, fiduciary financial advisor Sligo, financial advisor Wexford, financial planner Wexford, wealth management Wexford, investment advisor Wexford, retirement planning Wexford, financial consultant Wexford, personal finance advisor Wexford, financial planning services Wexford, independent financial advisor Wexford, fiduciary financial advisor Wexford"
        />
        <meta
          property="og:title"
          content="FAQ's - Apollo Financial Services | Apollo Financial Services Ireland"
        />
        <meta
          property="og:description"
          content="Expert answers to your financial planning questions about investments, pensions, tax strategies and wealth management in Ireland."
        />
        <meta
          property="og:url"
          content="https://www.apollofinancial.ie/faqs/"
        />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {`
        {
        "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is a financial advisor, and why do I need one?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "A financial advisor helps you plan and manage your financial future by providing expert guidance on investments, retirement, insurance, and tax strategies."
      }
    },
    {
      "@type": "Question",
      "name": "How do I schedule a free financial review?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Scheduling a free financial review is easy! Just contact us via phone or email."
      }
    },
    {
      "@type": "Question",
      "name": "Can you help with retirement planning?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes! We assist with pension management, annuities, and personalised retirement plans to ensure financial security."
      }
    },
    {
      "@type": "Question",
      "name": "How does tax optimisation work?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We implement legal tax strategies to reduce your tax burden, including tax-efficient investments and retirement account tax benefits."
      }
    },
    {
      "@type": "Question",
      "name": "Are your financial advisory services personalised?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Absolutely! We assess your financial situation, goals, and risk tolerance to create a tailored strategy that suits your needs."
      }
    },
    {
      "@type": "Question",
      "name": "Do I need a lot of money to start investing?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No, you don’t need a large sum to start investing. Apollo Financial Services helps clients with different financial backgrounds."
      }
    },
    {
      "@type": "Question",
      "name": "How often should I review my financial plan?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We recommend an annual financial review, but adjustments should be made whenever there’s a change in financial goals."
      }
    }
  ]
}
`}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://www.apollofinancial.ie/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "FAQ",
                item: "https://www.apollofinancial.ie/faq/",
              },
            ],
          })}
        </script>
      </Helmet>

      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              ${faqs
                .map(
                  (faq) => `{
                "@type": "Question",
                "name": "${faq.question.replace(/"/g, '"')}",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "${
                    typeof faq.answer.props.children[0]?.props?.children ===
                    "string"
                      ? faq.answer.props.children[0].props.children.replace(
                          /"/g,
                          '"'
                        )
                      : "Expert financial advice from Apollo Financial Services Ireland"
                  }"
                }
              }`
                )
                .join(",")}
            ]
          }
        `}
      </script>
      {/* Hero Section */}
      <motion.section
        ref={heroRef}
        className="faq-hero"
        initial="hidden"
        animate={heroInView ? "visible" : "hidden"}
        variants={fadeInVariants}
      >
        <div className="faq-hero-content">
          <motion.h1
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            Frequently Asked Questions
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Find out how Apollo Financial Services can help you make informed
            financial decisions.
          </motion.p>
        </div>
      </motion.section>

      {/* About Section */}
      <section className="faq-about">
        <h2>How Apollo Financial Services Can Help You</h2>
        <p>
          Apollo Financial Services offers personalised financial advice to help
          you navigate investments, retirement planning, tax strategies, and
          more. Our advisors work closely with you to ensure a secure and
          prosperous financial future.
        </p>
      </section>

      {/* FAQ Items */}
      <div className="faq-container">{renderedFAQs}</div>
    </div>
  );
};

export default FAQ;
